import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import Spinner from 'react16-spinjs';
import { READING_CHECK_CHOICES } from '../../troConstants';
import ShapedButton from '../../../common/components/ShapedButton';
import ReadingCheckSuccessCard from './ReadingCheckSuccessCard';
import { SPINNER } from '../../../constants/components';
import { ARIA_LABELS } from '../../../constants/text';

const MultiChoiceReadingCheckCard = props => {
    const {
        cleanup,
        didAttemptMultiChoiceReadingCheck,
        isScreenBlocked,
        isSpinnerVisible,
        narrateScreen,
        onFailure,
        onDone,
        playAvatarAudio,
        passedReadingCheck,
        patchDiscontinueResult,
        setPassedMultipleChoiceReadCheck,
        onSuccess,
        isExternalURLSourcePresent,
        trackPassedReadingCheck,
        trackTROFailedMultiChoiceReadingCheck,
        arLanguage,
    } = props;

    const [isSuccessCardActive, setSuccessCardActive] = useState(false);
    const [isPlayingSentence, setPlayingSentence] = useState(false);
    const [alreadyListened, setAlreadyListened] = useState(false);

    useEffect(() => {
        if (passedReadingCheck) {
            onDone();
        } else if (didAttemptMultiChoiceReadingCheck) {
            setSuccessCardActive(passedReadingCheck);
            if (passedReadingCheck) {
                if(!isExternalURLSourcePresent) trackPassedReadingCheck();
                onSuccess();
            } else {
                onFailure();
            }
        } else {
            narrateScreen(isExternalURLSourcePresent, arLanguage);
        }
        return () => {
            cleanup();
        };
    }, []);

    useEffect(() => {
        if (didAttemptMultiChoiceReadingCheck) {
            setSuccessCardActive(passedReadingCheck);
            if (passedReadingCheck) {
                if(!isExternalURLSourcePresent) trackPassedReadingCheck();
                onSuccess();
            } else {
                onFailure();
            }
        }
    }, [didAttemptMultiChoiceReadingCheck, passedReadingCheck]);

    const successCard = (
        <ReadingCheckSuccessCard text="Great Job!" onDone={onDone} />
    );

    const checkAnswer = isCorrect => {
        if (isCorrect) {
            setPassedMultipleChoiceReadCheck(isCorrect);
            setSuccessCardActive(isCorrect);
        } else {
            if (!isExternalURLSourcePresent) trackTROFailedMultiChoiceReadingCheck();
            patchDiscontinueResult().then(() => {
                setPassedMultipleChoiceReadCheck(isCorrect);
                setSuccessCardActive(isCorrect);
            });
        }
    };

    const playSentence = () => {
        setPlayingSentence(true);
        setAlreadyListened(true);
        playAvatarAudio(isExternalURLSourcePresent, arLanguage).then(() => {
            setPlayingSentence(false);
        });
    };

    const showOverlay = isScreenBlocked || isSpinnerVisible || isPlayingSentence || !alreadyListened;
    const showSpeakerButton = !alreadyListened && !isScreenBlocked;

    const promptCard = (
        // eslint-disable-next-line react/jsx-fragments
        <React.Fragment>
            {showOverlay && <div className="reading-overlay" />}
            <div className="reading-check-card">
                <div className="multiple-choice-container">
                    <div
                        className={classNames('amp-styles', 'speaker-button-container', {
                            'above-overlay': showSpeakerButton,
                        })}
                    >
                        <ShapedButton
                            ariaLabel={ARIA_LABELS.PLAY_AUDIO}
                            styles={classNames('icon-v2-volume-up', 'speaker-button', {
                                disabled: isScreenBlocked,
                            })}
                            isEnabled={!isScreenBlocked}
                            onClick={playSentence}
                        />
                    </div>
                    {Object.values(READING_CHECK_CHOICES).map(choice => {
                        const { text, isCorrect } = choice;
                        return (
                            <ShapedButton
                                ariaLabel={text}
                                onClick={() => checkAnswer(isCorrect)}
                                key={text}
                                text={text}
                                styles={classNames('multiple-choice-button', {
                                    disabled: (isScreenBlocked || !alreadyListened),
                                })}
                                isEnabled={!isScreenBlocked && alreadyListened}
                            />
                        );
                    })}
                </div>
                {isSpinnerVisible && <Spinner config={SPINNER.DARK} />}
            </div>
        </React.Fragment>
    );

    if (isSuccessCardActive) {
        return successCard;
    }

    return promptCard;
};

MultiChoiceReadingCheckCard.propTypes = {
    cleanup: PropTypes.func.isRequired,
    didAttemptMultiChoiceReadingCheck: PropTypes.bool.isRequired,
    isScreenBlocked: PropTypes.bool.isRequired,
    isSpinnerVisible: PropTypes.bool,
    narrateScreen: PropTypes.func.isRequired,
    onFailure: PropTypes.func.isRequired,
    onDone: PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired,
    playAvatarAudio: PropTypes.func.isRequired,
    passedReadingCheck: PropTypes.bool.isRequired,
    patchDiscontinueResult: PropTypes.func.isRequired,
    setPassedMultipleChoiceReadCheck: PropTypes.func.isRequired,
    trackPassedReadingCheck: PropTypes.func.isRequired,
    trackTROFailedMultiChoiceReadingCheck: PropTypes.func.isRequired,
    arLanguage: PropTypes.string.isRequired,
    isExternalURLSourcePresent: PropTypes.bool.isRequired,
};

MultiChoiceReadingCheckCard.defaultProps = {
    isSpinnerVisible: false,
};

export default MultiChoiceReadingCheckCard;
